import { z } from "zod";

export const LLM_MODEL_PRICE_SOURCES = ["models.dev", "runtime_flag"] as const;
export type LlmModelPriceSource = (typeof LLM_MODEL_PRICE_SOURCES)[number];
export const DEFAULT_MODELS_DEV_FETCH_TIMEOUT_MS = 15_000;

export const llmModelPriceSourceSchema = z.enum(LLM_MODEL_PRICE_SOURCES);

export const llmPricingOverrideSchema = z
  .object({
    provider: z.string().trim().min(1),
    model: z.string().trim().min(1),
    inputPerMillion: z.number().nonnegative().nullable(),
    outputPerMillion: z.number().nonnegative().nullable(),
    cacheReadPerMillion: z.number().nonnegative().nullable(),
    cacheWritePerMillion: z.number().nonnegative().nullable(),
  })
  .strict();

export const llmPricingOverridesSchema = z.array(llmPricingOverrideSchema);

export type LlmPricingOverride = z.infer<typeof llmPricingOverrideSchema>;

export interface LlmModelPriceSyncItem {
  provider: string;
  model: string;
  source: LlmModelPriceSource;
  inputPerMillion: string | null;
  outputPerMillion: string | null;
  cacheReadPerMillion: string | null;
  cacheWritePerMillion: string | null;
  sourceFetchedAt: Date;
}

const toNullableRateString = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return value.toFixed(6);
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (normalized.length === 0) {
      return null;
    }

    const parsed = Number(normalized);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed.toFixed(6);
    }
  }

  return null;
};

interface ModelsDevModelRecord {
  cost?: Record<string, unknown>;
}

interface ModelsDevProviderRecord {
  models?: Record<string, ModelsDevModelRecord>;
}

interface ModelsDevApiResponse {
  [provider: string]: ModelsDevProviderRecord | unknown;
}

export const parseModelsDevPrices = (
  payload: unknown,
  fetchedAt: Date,
): LlmModelPriceSyncItem[] => {
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const items: LlmModelPriceSyncItem[] = [];
  const providers = payload as ModelsDevApiResponse;

  for (const [provider, rawProviderValue] of Object.entries(providers)) {
    if (!rawProviderValue || typeof rawProviderValue !== "object") {
      continue;
    }

    const providerValue = rawProviderValue as ModelsDevProviderRecord;
    if (!providerValue.models || typeof providerValue.models !== "object") {
      continue;
    }

    for (const [model, modelRecord] of Object.entries(providerValue.models)) {
      if (!modelRecord || typeof modelRecord !== "object") {
        continue;
      }

      const cost = modelRecord.cost;
      if (!cost || typeof cost !== "object") {
        continue;
      }

      const inputPerMillion = toNullableRateString(cost.input);
      const outputPerMillion = toNullableRateString(cost.output);
      const cacheReadPerMillion = toNullableRateString(cost.cache_read);
      const cacheWritePerMillion = toNullableRateString(cost.cache_write);

      if (
        inputPerMillion === null &&
        outputPerMillion === null &&
        cacheReadPerMillion === null &&
        cacheWritePerMillion === null
      ) {
        continue;
      }

      items.push({
        provider,
        model,
        source: "models.dev",
        inputPerMillion,
        outputPerMillion,
        cacheReadPerMillion,
        cacheWritePerMillion,
        sourceFetchedAt: fetchedAt,
      });
    }
  }

  return items;
};

const toOverrideRateString = (value: number | null): string | null =>
  value === null ? null : value.toFixed(6);

const pricingRowKey = (provider: string, model: string): string => `${provider}::${model}`;

export const mergePricingOverridePrices = (input: {
  importedPrices: LlmModelPriceSyncItem[];
  pricingOverrides: LlmPricingOverride[];
  syncedAt: Date;
}): LlmModelPriceSyncItem[] => {
  const merged = new Map(
    input.importedPrices.map((item) => [pricingRowKey(item.provider, item.model), item] as const),
  );

  for (const override of input.pricingOverrides) {
    merged.set(pricingRowKey(override.provider, override.model), {
      provider: override.provider,
      model: override.model,
      source: "runtime_flag",
      inputPerMillion: toOverrideRateString(override.inputPerMillion),
      outputPerMillion: toOverrideRateString(override.outputPerMillion),
      cacheReadPerMillion: toOverrideRateString(override.cacheReadPerMillion),
      cacheWritePerMillion: toOverrideRateString(override.cacheWritePerMillion),
      sourceFetchedAt: input.syncedAt,
    });
  }

  return Array.from(merged.values());
};
